// modulePrefs.js — per-person preferences for workspace MODULES
// (MODULE-USER-PREFS-1). Reached through the module data bridge:
//
//   await el.call('moduleData', 'prefs', 'get',    { key: 'adjustOpen' })  // → value | null
//   await el.call('moduleData', 'prefs', 'list',   {})                     // → { key: value, … }
//   await el.call('moduleData', 'prefs', 'set',    { key: 'adjustOpen', value: true })
//   await el.call('moduleData', 'prefs', 'remove', { key: 'adjustOpen' })
//
// STORAGE — the caller's own user-preferences row (`workspaceProject.
// userPreferences`, GET/PUT /core/prefs/user). The server scopes that row by
// the CALLER's identity and nothing else (PrefsService.getUserPrefs /
// upsertUserPrefs, fail-closed on the user), so no other member can read or
// write it — the privacy between PEOPLE comes from the server, not from this
// file. No server change was needed: the row is a Mixed bag that already
// persists ad-hoc nested keys.
//
// LAYOUT inside the row: `modulePrefs[<workspaceId>][<moduleId>][<key>]`.
//   • workspaceId — the ACTIVE workspace (root.workspace.id).
//   • moduleId — the installed module that owns the CALLING element's scope
//     context (workspaceProjects.js `moduleIdOfContext`).
//   Both are stored percent-encoded (`_storeKey`), so an id with a `.` can
//   never meet the server's key sanitizer (below).
//
// ⚠ MODULE ISOLATION IS NOT ENFORCED. The module id comes from the caller's
// element context, and every module runs in ONE shared JS context with the
// shell: module A can reach module B's element or context (or read
// `root.userPrefs` outright) and so read, write or remove B's prefs. The
// partition keeps honest modules apart and stops accidents; it is not a
// security boundary, and nothing here may rely on it as one. That is accepted
// because there is no boundary to enforce between modules that share a realm —
// the boundary that matters, between people, is the server's.
//
// THE SERVER SANITIZER drops keys silently. The API's `mongoSanitize`
// (server src/core/middleware/mongoSanitize.js) deletes every object KEY that
// starts with `$` or contains `.` from a request body, at any depth up to 12,
// and the PUT still answers 200 — a `set` resolved and a fresh session read
// null (ws crm review, 2026-10-09). So: a pref key is
// `^[A-Za-z][A-Za-z0-9_-]{0,63}$` (no `.`), and a value whose nested object
// keys contain `.` or start with `$` is REFUSED here, loudly, before any write.
//
// LIMITS: value must be plain JSON (stored as its JSON round-trip); one
// module's bag ≤ 16 KB of UTF-8 JSON in one workspace; every module bag of the
// person together ≤ 256 KB, so module data can never crowd out the shell's own
// preferences in the shared row. `remove` is always allowed — it is the way
// back under a limit.
//
// WRITES are whole-row PUTs (the server $sets the row), so they ride the SAME
// per-workspace queue every other shell user-pref writer uses
// (`root.__navbarSaveQueue`, see assistantMode.js) and merge onto the live
// `root.userPrefs` at send time — a write never drops another writer's keys in
// THIS tab. (Two tabs still race: the last whole-row PUT wins.) The local copy
// updates first, so a re-read right after a set sees it; when the PUT fails
// the local change is rolled back, unless a later call changed the key since.

export const MODULE_PREFS_ROW_KEY = 'modulePrefs'
export const MODULE_PREFS_MAX_BYTES = 16 * 1024
export const MODULE_PREFS_TOTAL_MAX_BYTES = 256 * 1024
export const MODULE_PREFS_KEY_RE = /^[A-Za-z][A-Za-z0-9_-]{0,63}$/
const OPS = new Set(['get', 'list', 'set', 'remove'])

const _isObj = (v) => !!v && typeof v === 'object' && !Array.isArray(v)

const _bytes = (v) => {
  const s = JSON.stringify(v)
  return typeof TextEncoder === 'function' ? new TextEncoder().encode(s).length : s.length * 3
}

const _wsIdOf = (root) => {
  const w = root && root.workspace
  const id =
    (w && (w.id || w._id)) || (root && root.jwtClaims && root.jwtClaims.active_workspace_id)
  return id ? String(id) : ''
}

// The JSON round-trip of a value, or an Error naming why it is not plain JSON.
const _plainJson = (value) => {
  if (value === undefined) return new Error('[module] prefs.set requires args.value')
  let s
  try {
    s = JSON.stringify(value)
  } catch (err) {
    return new Error(`[module] prefs value is not JSON: ${err && err.message}`)
  }
  if (typeof s !== 'string') return new Error('[module] prefs value is not JSON')
  const parsed = JSON.parse(s)
  const bad = _forbiddenKeyIn(parsed, 0)
  if (bad !== null) {
    return new Error(
      `[module] prefs value has the object key "${bad}" — keys with "." or a leading "$" are dropped by the server; rename it.`
    )
  }
  return parsed
}

// The first object key (at any depth) the server's sanitizer would drop, or
// null. Same test as server mongoSanitize.hasForbiddenKey.
const _forbiddenKeyIn = (v, depth) => {
  if (v === null || typeof v !== 'object' || depth > 64) return null
  if (Array.isArray(v)) {
    for (const item of v) {
      const bad = _forbiddenKeyIn(item, depth + 1)
      if (bad !== null) return bad
    }
    return null
  }
  for (const k of Object.keys(v)) {
    if (k.startsWith('$') || k.includes('.')) return k
    const bad = _forbiddenKeyIn(v[k], depth + 1)
    if (bad !== null) return bad
  }
  return null
}

// A workspace / module id as a row key: percent-encoded, `.` included, so the
// server sanitizer can never drop a whole bag for its id.
const _storeKey = (id) => encodeURIComponent(String(id)).replace(/\./g, '%2E')

const _bagOf = (prefs, wsId, moduleId) => {
  const all = prefs && _isObj(prefs[MODULE_PREFS_ROW_KEY]) ? prefs[MODULE_PREFS_ROW_KEY] : null
  const ws = all && _isObj(all[wsId]) ? all[wsId] : null
  return ws && _isObj(ws[moduleId]) ? ws[moduleId] : {}
}

// The row with one module bag replaced (an empty bag is dropped), or an Error
// when a limit is crossed. Pure — the inputs are never mutated.
// `{ caps: false }` skips both limits — a remove or a roll-back only shrinks
// the row and must work for a person who is already over a limit.
export const withModuleBag = (prefs, wsId, moduleId, bag, { caps = true } = {}) => {
  const base = _isObj(prefs) ? prefs : {}
  const all = _isObj(base[MODULE_PREFS_ROW_KEY]) ? base[MODULE_PREFS_ROW_KEY] : {}
  const ws = { ...(_isObj(all[wsId]) ? all[wsId] : {}) }
  if (bag && Object.keys(bag).length) ws[moduleId] = bag
  else delete ws[moduleId]
  const nextAll = { ...all }
  if (Object.keys(ws).length) nextAll[wsId] = ws
  else delete nextAll[wsId]
  if (!caps) return { ...base, [MODULE_PREFS_ROW_KEY]: nextAll }
  if (bag && _bytes(bag) > MODULE_PREFS_MAX_BYTES) {
    return new Error(
      `[module] prefs for this module exceed ${MODULE_PREFS_MAX_BYTES} bytes — store less, or keep data in records`
    )
  }
  if (_bytes(nextAll) > MODULE_PREFS_TOTAL_MAX_BYTES) {
    return new Error(
      `[module] module prefs of this person exceed ${MODULE_PREFS_TOTAL_MAX_BYTES} bytes in total`
    )
  }
  return { ...base, [MODULE_PREFS_ROW_KEY]: nextAll }
}

// A name no other catalog library hoists: frank lifts this helper into the
// library's ONE flat globalScope, where marketplace's own `_copy` (the hero
// copy lookup) already sits (globalScopeNameCensus).
const _modulePrefCopy = (v) => (v === undefined ? null : JSON.parse(JSON.stringify(v)))

// One prefs call. `this` is the calling element (el.call binds it).
// Options: `root` (the shell root), `sdk` (the SDK instance), `moduleIdOf`.
export function modulePrefsCall(op, args, { root, sdk, moduleIdOf } = {}) {
  const operation = String(op || '').toLowerCase()
  if (!OPS.has(operation)) {
    return Promise.reject(
      new Error(`[module] prefs operation "${op}" is not permitted — only get/list/set/remove.`)
    )
  }
  const moduleId = typeof moduleIdOf === 'function' ? moduleIdOf(this && this.context) : null
  if (!moduleId) {
    return Promise.reject(
      new Error('[module] prefs: the caller is not inside an installed module.')
    )
  }
  const rawWsId = _wsIdOf(root)
  if (!rawWsId) return Promise.reject(new Error('[module] prefs: no active workspace.'))
  const wsId = _storeKey(rawWsId)
  const modKey = _storeKey(moduleId)
  const key = args && args.key
  if (operation !== 'list' && (typeof key !== 'string' || !MODULE_PREFS_KEY_RE.test(key))) {
    return Promise.reject(
      new Error(
        `[module] prefs key "${String(key)}" is invalid — use 1-64 chars: a letter, then letters, digits, "_" or "-".`
      )
    )
  }
  if (!sdk || typeof sdk.execute !== 'function') {
    return Promise.reject(new Error('[module] SDK unavailable.'))
  }
  const loaded = () => (root && _isObj(root.userPrefs) ? root.userPrefs : null)
  const readRow = async () => {
    const live = loaded()
    if (live) return live
    const got = await sdk.execute('workspaceProject.userPreferences', 'get')
    return _isObj(got) ? got : {}
  }

  if (operation === 'get' || operation === 'list') {
    return readRow().then((row) => {
      const bag = _bagOf(row, wsId, modKey)
      if (operation === 'list') return _modulePrefCopy(bag)
      return Object.prototype.hasOwnProperty.call(bag, key) ? _modulePrefCopy(bag[key]) : null
    })
  }

  let value
  if (operation === 'set') {
    value = _plainJson(args && args.value)
    if (value instanceof Error) return Promise.reject(value)
  }
  const change = (row) => {
    const bag = { ..._bagOf(row, wsId, modKey) }
    if (operation === 'set') bag[key] = value
    else delete bag[key]
    return withModuleBag(row, wsId, modKey, bag, { caps: operation === 'set' })
  }

  // Validate and apply locally first, so a set is visible to the next get.
  // Remember what the key held, for the roll-back below.
  const now = loaded()
  const before = now ? _bagOf(now, wsId, modKey) : null
  const had = !!before && Object.prototype.hasOwnProperty.call(before, key)
  const prevValue = had ? _modulePrefCopy(before[key]) : undefined
  if (now) {
    const next = change(now)
    if (next instanceof Error) return Promise.reject(next)
    if (typeof root.update === 'function') root.update({ userPrefs: next }, { preventFetch: true })
  }
  const ours = JSON.stringify(operation === 'set' ? value : undefined)
  const rollBack = () => {
    const cur = loaded()
    if (!now || !cur || typeof root.update !== 'function') return
    const bag = { ..._bagOf(cur, wsId, modKey) }
    const has = Object.prototype.hasOwnProperty.call(bag, key)
    // A later call changed the key since — its value stands.
    if (JSON.stringify(has ? bag[key] : undefined) !== ours) return
    if (had) bag[key] = prevValue
    else delete bag[key]
    root.update(
      { userPrefs: withModuleBag(cur, wsId, modKey, bag, { caps: false }) },
      { preventFetch: true }
    )
  }

  if (!root.__navbarSaveQueue) root.__navbarSaveQueue = new Map()
  const queue = root.__navbarSaveQueue
  const qKey = rawWsId || 'user'
  const prior = queue.get(qKey) || Promise.resolve()
  const task = prior
    .catch(() => {})
    .then(async () => {
      // `change(row)` still runs — it is how the byte-budget caps get
      // checked against a fresh row on a cold write (`now` unset below) — but
      // its RESULT is no longer the wire payload.
      const row = await readRow()
      const validated = change(row)
      if (validated instanceof Error) throw validated
      // PER-PATH PATCH — the path is an ARRAY OF SEGMENTS (never a dotted
      // string: the server's sanitizer drops a dotted key silently), and it
      // names only THIS module's (workspace, module, key) tuple, so a
      // different module, workspace or key is never overwritten
      // (CORE-USERPREFS-PATCH-PER-PATH-1). A `remove` that empties the bag —
      // or the workspace's last bag — collapses the EMPTY PARENT instead of
      // the leaf, the same cleanup `withModuleBag` already does locally
      // (one path only: never the leaf AND an ancestor in the same call).
      let unsetPath = [MODULE_PREFS_ROW_KEY, wsId, modKey, key]
      if (operation === 'remove') {
        const bagNow = _bagOf(row, wsId, modKey)
        if (!Object.keys(bagNow).some((k) => k !== key)) {
          const all = row && _isObj(row[MODULE_PREFS_ROW_KEY]) ? row[MODULE_PREFS_ROW_KEY] : {}
          const wsBag = _isObj(all[wsId]) ? all[wsId] : {}
          unsetPath = Object.keys(wsBag).some((m) => m !== modKey)
            ? [MODULE_PREFS_ROW_KEY, wsId, modKey]
            : [MODULE_PREFS_ROW_KEY, wsId]
        }
      }
      await sdk.execute('workspaceProject.userPreferences', 'patch', {
        patch:
          operation === 'set'
            ? { set: [{ path: [MODULE_PREFS_ROW_KEY, wsId, modKey, key], value }] }
            : { unset: [unsetPath] }
      })
      // A cold write (row not loaded before) lands in the local copy only if
      // the boot read has filled it meanwhile; never invent the whole row.
      const after = loaded()
      if (after && typeof root.update === 'function') {
        const merged = change(after)
        if (!(merged instanceof Error)) root.update({ userPrefs: merged }, { preventFetch: true })
      }
      return operation === 'set' ? _modulePrefCopy(value) : null
    })
    .catch((err) => {
      rollBack()
      throw err
    })
  queue.set(qKey, task)
  return task
}
